/* eslint-disable react/jsx-no-target-blank */
import React from "react";
import classnames from "classnames";
import { FormattedMessage, injectIntl } from "react-intl";
import {ReactComponent as DealerOnLogo} from "../images/dealeron-logo-light.svg";
import { Card, CardText } from "@sincro/react-card";
import {
  NotificationMenu,
  SettingsMenu,
  UserMenu,
} from "@sincro/react-global-header-and-navigation";
import { PageContainer } from "@sincro/react-page-container";
import { PageContent } from "@sincro/react-page-content";
import { PageHeader, PageHeaderTitle } from "@sincro/react-page-header";
import { TrackedElement } from "@sincro/react-tracked-element";
import { Body2, CaptionText, Headline5 } from "@sincro/react-typography";

import stayInLoopData from "../data/stayInLoop.json";
import { Announcements } from "../shared/Announcements";
import { ApplicationCards } from "../shared/Cards/ApplicationCards";
import { StayInTheLoopCard } from "../shared/Cards/StayInTheLoopCard";
import {
  getDccDashboardUrl,
  getLoginServiceApiUrl,
  getUserServiceApiUrl,
  getWsmUrl,
} from "../shared/services/AppService";
import { makeRequest } from "../shared/services/ApiHandler";
import { injectDigitalDataForGtm } from "../shared/analytics/Loader";
import { makePascalCase } from "../shared/analytics/AnalyticsUtil";

export class App extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      locale: props.locale || "en-US",
      user: {},
      apps: props.apps || [],
    };
  }

  getUsernameDetails = () => {
    const apiUrl = getUserServiceApiUrl();

    return makeRequest(apiUrl).catch((err) => {
      if (err) {
        if (err.response && err.response.status === 401) {
          window.location = getLoginServiceApiUrl();
        }
      }
    });
  };

  isValidUserObject = (user) =>
    typeof user === `object` && Object.keys(user).length !== 0;

  componentDidMount = async () => {
    const user = await this.getUsernameDetails();
    if (this.isValidUserObject(user)) {
      this.setState({ user });
    }
    injectDigitalDataForGtm();

    // Set digitalData values
    if (window.digitalData) {
      if (this.isValidUserObject(user) && window.digitalData.setUser) {
        window.digitalData.setUser({
          name: `${user.firstName} ${user.lastName}`,
          type: user.isInternal ? "CBLT_User" : "External_User",
        });
      }

      window.digitalData.setPage &&
        window.digitalData.setPage({
          pageInfo: {
            pageName: "Digital Command Center",
            url: window.location,
          },
        });
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.user !== this.state.user ||
      prevProps.apps !== this.props.apps
    ) {
      this.setState({ apps: this.props.apps });
    }
  }

  onSelectLocale = (val) => {
    this.props.updateLocale(val);
  };

  render() {
    const stayInLoopCards = stayInLoopData.stayInLoop || [];
    const appWsmUrl = getWsmUrl();
    return (
      <PageContainer
        className={classnames("pageContainer", {
          empty: !this.props.isAuthenticated,
        })}
      >
        {this.props.isAuthenticated && (
          <>
            <PageHeader className="pageHeader">
              <div className="pageHeader-contents">
                <div className="pageHeader-logo">
                  <DealerOnLogo />
                </div>
                <PageHeaderTitle>
                  <FormattedMessage
                    id="Dcc.pageHeaderTitle"
                    defaultMessage="Digital Command Center"
                  />
                </PageHeaderTitle>
                <div className="pageHeader-toolbar">
                  <NotificationMenu notifications={this.props.notifications} />
                  <SettingsMenu
                    selectedValue={this.state.locale}
                    onSelect={this.onSelectLocale}
                  />
                  <UserMenu
                    user={this.state.user}
                    onLogoutReturnUrl={getDccDashboardUrl()}
                  />
                </div>
              </div>
            </PageHeader>
            <PageContent>
              <Announcements announcements={this.props.announcements} />
              <ApplicationCards
                apps={this.state.apps}
                isAuthenticated={this.props.isAuthenticated}
              />
              <Headline5>
                <FormattedMessage
                  id="Dcc.stayInTheloop"
                  defaultMessage="Stay in the Loop"
                />
              </Headline5>
              <div className="stayInLoopContainer">
                <div className="stayInLoop dcc-container-dark">
                  {stayInLoopCards.map((card) => {
                    return (
                      <TrackedElement
                        key={card.id}
                        type={`navigate|${makePascalCase(card.id)}`}
                        name="DCC|StayInTheLoop"
                      >
                        <StayInTheLoopCard
                          image={require("../images/" + card.imageUrl)}
                          name={card.name}
                          url={card.path}
                          text={this.props.intl.formatMessage({
                            id: card.i18nText,
                            defaultMessage: card.text,
                          })}
                          title={this.props.intl.formatMessage({
                            id: card.i18nId,
                            defaultMessage: card.id,
                          })}
                        />
                      </TrackedElement>
                    );
                  })}
                </div>
              </div>
              <Headline5>
                <FormattedMessage
                  id="Dcc.needSupport"
                  defaultMessage="Need Support?"
                />
              </Headline5>
              <div className="needSupport dcc-container-dark">
                <Card className="needSupportCard">
                  <CardText>
                    <CaptionText>
                      <FormattedMessage
                        id="Dcc.needSupportEmailSupportDescription"
                        defaultMessage="To request support for all other applications, please email <a>Help@DealerOn.com</a>"
                        values={{
                          a: (chunks) => (
                            <TrackedElement
                              type="email|AnsiraInTheMoment"
                              name="DCC|NeedSupport"
                            >
                              <a href="mailto:Help@DealerOn.com">
                                {chunks}
                              </a>
                            </TrackedElement>
                          ),
                        }}
                      />
                    </CaptionText>
                    <p>
                    </p>
                    <CaptionText>
                      <FormattedMessage
                        id="Dcc.needSupportSalesDescription"
                        defaultMessage="To speak with someone directly, please call <a> 800-909-8244 </a>"
                        values={{
                          br: <br />,
                          a: (chunks) => {
                            return (
                              <TrackedElement
                                type="call|Support"
                                name="DCC|NeedSupport"
                              >
                                <a href="tel:(+1)800-909-8244"> {chunks} </a>
                              </TrackedElement>
                            );
                          },
                        }}
                      />
                    </CaptionText>
                  </CardText>
                </Card>
              </div>
              <footer className="sincro-dcc-footer">
                <Body2>
                  &copy;{new Date().getFullYear()} DealerOn, Inc.&nbsp;
                  <FormattedMessage
                    id="Dcc.allRightsReserved"
                    defaultMessage="All rights reserved. "
                  />
                  <FormattedMessage
                    id="Dcc.footerDescription"
                    defaultMessage="This website is for the exclusive use of authorized customers. Unauthorized access is prohibited. Usage will be monitored."
                    values={{
                      br: <br />,
                      a: (chunks) => {
                        return (
                          <TrackedElement
                            type="navigate|ProductGuide"
                            name="DCC"
                          >
                            <a
                              href="https://www.ansira.com/website-terms-use"
                              target="_blank"
                            >
                              {chunks}
                            </a>
                          </TrackedElement>
                        );
                      },
                    }}
                  />
                </Body2>
              </footer>
            </PageContent>
          </>
        )}
      </PageContainer>
    );
  }
}

export default injectIntl(App);
